import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message || "If an account exists, a reset link has been sent.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden select-none bg-cover bg-center"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1770791779732-7d1ef12524f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBncmFkaWVudCUyMGJhY2tncm91bmQlMjByZWQlMjBibGFja3xlbnwwfHx8fDE3NzgzOTI5NjF8MA&ixlib=rb-4.1.0&q=85')"
      }}
    >
      {/* Heavy dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/85 to-red-950/40 z-0" />

      {/* Main glass card wrapper */}
      <div className="relative w-full max-w-md z-10 p-0.5 rounded-[2rem] overflow-hidden bg-gradient-to-b from-white/15 to-transparent">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="backdrop-blur-2xl bg-black/40 border border-white/10 shadow-2xl rounded-[1.95rem] p-8 md:p-10 text-white"
          data-testid="forgot-card"
        >
          <div className="mb-7 flex items-center justify-between">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-300 transition hover:text-white">
              <ArrowLeft size={14} /> Back to login
            </Link>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 px-2.5 py-1 text-[10px] font-semibold text-blue-300">
              <ShieldCheck size={12} className="text-blue-400" />
              Secure Recovery
            </div>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-lg shadow-blue-900/40"
              style={{ background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)" }}
            >
              <span className="font-bold text-lg font-display leading-none">H</span>
            </div>
            <div>
              <div className="font-display text-lg font-bold leading-none tracking-tight">Hoardexa</div>
              <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.2em] text-blue-400">Account Recovery</div>
            </div>
          </div>

          <div className="space-y-1.5">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white">Reset Password</h2>
            <p className="text-xs text-gray-300 leading-relaxed">
              We'll generate a secure link to reset your password.
            </p>
          </div>

          {!sent ? (
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <label htmlFor="forgot-email" className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">Email Address</label>
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3.5 py-3 transition-all duration-200 focus-within:border-red-500/50 focus-within:bg-white/10 focus-within:ring-4 focus-within:ring-blue-500/10">
                  <Mail size={16} className="text-gray-400" />
                  <input
                    data-testid="forgot-email"
                    id="forgot-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-gray-500"
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/30 hover:from-blue-500 hover:to-blue-600 transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:scale-100"
              >
                {loading ? "Sending..." : <>Send Reset Link <ArrowRight size={15} /></>}
              </button>
            </form>
          ) : (
            <div className="mt-8 space-y-6">
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0 text-emerald-400" />
                  <div>
                    <div className="text-sm font-semibold text-emerald-300">Link Generated</div>
                    <p className="mt-1 text-xs leading-relaxed text-emerald-200">
                      If an account exists for <b className="font-semibold text-white">{email}</b>, we have sent a reset password link.
                    </p>
                  </div>
                </div>
              </div>
              <Link 
                to="/login" 
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/10"
              >
                <ArrowLeft size={15} /> Back to login
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
